<script setup lang="ts">
import { ElDialog } from 'element-plus'

import { provideLoadingState } from '@/shared/lib'

/**
 * The dialog. A create/edit form goes in a `FormModal`, which is this with the form, the
 * errors and the footer already in it; reach for `Modal` for anything that is not a form.
 *
 * **It asks to be closed, it does not close itself** — `@close` is a request, and whoever
 * owns the open state answers it by flipping `v-model`. That is what lets a modal stay open
 * through work it must not abandon.
 *
 * `loading` masks the body *and* announces to everything inside that the record has not
 * arrived, so a select in there can hold its request back until it can seed itself.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: boolean
    title?: string
    width?: string | number
    closeOnClickModal?: boolean
    loading?: boolean
    /** Element Plus defaults to 15vh, which pushes taller forms off the screen. */
    top?: string
  }>(),
  { top: '5vh', closeOnClickModal: false }
)

provideLoadingState(() => props.loading)

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'close'): void
}>()

/**
 * Never calls element-plus's `done()`: that hides the dialog whatever the owner says, so a
 * modal that has to stay open loses its dialog while its route and component stay mounted —
 * and with `model-value` unchanged, it can never be shown again.
 */
const handleBeforeClose = () => emit('close')
</script>

<template>
  <ElDialog
    class="app-modal"
    :model-value="modelValue"
    :title="title"
    :width="width"
    :top="top"
    :close-on-click-modal="closeOnClickModal"
    :before-close="handleBeforeClose"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!-- The gap between what a modal stacks is the modal's own, so a caller adds no margins.
         Spaced by margins and **not** by a flex `Stack`: a column flex box stretches its
         items, which blows a button placed straight into a modal out to the full width of
         the dialog. A child that renders nothing leaves a comment node, which is not an
         element and so is not the `:first-child` the rule skips. -->
    <div v-loading="loading" class="app-modal__body">
      <slot />
    </div>
    <template #footer>
      <slot name="footer" />
    </template>
  </ElDialog>
</template>

<style lang="scss">
// Not scoped: the children spaced here are slot content, which carries the *caller's*
// scope id and never this component's.
.app-modal__body > :not(:first-child, .el-loading-mask) {
  margin-top: 12px;
}

// `v-loading` sits on the body, so its mask would cover the body alone — the title, the
// close button and the footer's buttons stayed live beside a spinner. The directive makes
// its host `position: relative`; undoing that here hands the absolutely positioned mask to
// the nearest positioned ancestor, which is `.el-dialog` itself, so it covers the whole
// dialog and follows its rounded corners.
.app-modal__body.el-loading-parent--relative {
  position: static !important;
}

.app-modal__body > .el-loading-mask {
  border-radius: var(--el-dialog-border-radius);
}

@media (max-width: $bp-mobile) {
  .app-modal.el-overlay-dialog .el-dialog,
  .app-modal .el-dialog,
  .app-modal.el-dialog {
    --el-dialog-width: 90vw !important;
    width: 90vw !important;
    max-width: 90vw !important;
    margin: 5vh auto !important;
  }
}
</style>
